import { ICertificate } from "../_services/interface/certificate.interface";

export type Jurisdiction = "federal" | "state" | "municipal";

export const JURISDICTIONS: Jurisdiction[] = ["federal", "state", "municipal"];

/**
 * Fonte única da leitura de jurisdição do CATÁLOGO de certidões. Antes da
 * GEN-3157 a recursão pelos `children` estava duplicada em `routes/routeAccess.ts`
 * e em `components/sidebar.tsx` — duas cópias da mesma regra que precisavam
 * concordar para o menu e o guarda de rota nunca divergirem.
 */

/** Este tipo (ou algum descendente) pertence a `jurisdiction`? */
export const certificateHasJurisdiction = (certificate: ICertificate, jurisdiction: Jurisdiction): boolean => {
  if (certificate.jurisdiction === jurisdiction) {
    return true;
  }
  return !!certificate.children?.some((child) => certificateHasJurisdiction(child, jurisdiction));
};

/** Todas as jurisdições presentes na árvore do tipo (raiz + descendentes). */
export const certificateJurisdictions = (certificate: ICertificate): Jurisdiction[] =>
  JURISDICTIONS.filter((j) => certificateHasJurisdiction(certificate, j));

/**
 * A jurisdição ÚNICA de um tipo de certidão, quando ela existe — ex.: CNDT e FGTS
 * são só federais; SINTEGRA, TRT e TRF são só estaduais. Tipos que cobrem mais de
 * uma jurisdição (CND e CADIN: federal+estadual+municipal) e
 * tipos sem jurisdição declarada retornam `undefined`: são ambíguos e não devem
 * restringir nada.
 *
 * GEN-3157 usa isto só para decidir QUAIS CAMPOS a barra de filtros exibe. NÃO
 * deve alimentar a consulta ao backend nem a chave de layout da tabela: a
 * jurisdição gravada num registro pode divergir da do catálogo (há registros
 * `sintegra` com jurisdiction=federal no banco), e derivá-la para a query os
 * removeria da lista.
 */
export const certificateSingleJurisdiction = (certificate?: ICertificate): Jurisdiction | undefined => {
  if (!certificate) {
    return undefined;
  }
  const found = certificateJurisdictions(certificate);
  return found.length === 1 ? found[0] : undefined;
};

/**
 * Tipos que, por decisão de PRODUTO, nunca têm os campos restringidos — seguem com
 * a barra completa (Jurisdição + Estado + Cidade) mesmo quando o catálogo declara
 * uma jurisdição única para eles:
 *
 * - `outros` — é o tipo curinga, usado para cadastrar certidões/consultas que o
 *   sistema ainda não tem como tipo próprio. Entram registros de qualquer esfera,
 *   e a intenção de produto é justamente evoluí-lo para esses usos.
 * - `divida-ativa` — dívida ativa MUNICIPAL está no roadmap; tirar os campos agora
 *   só para devolvê-los depois é churn na tela do usuário.
 * - `trt` / `trf` — tribunais são nominalmente estaduais, mas a competência de uma
 *   região pode recortar municípios específicos dentro do estado.
 *
 * SINTEGRA e TJSP-Falências NÃO entram: são estaduais sem recorte municipal
 * possível, e lá o filtro de Cidades só devolveria vazio.
 *
 * ⚠️ Jurisdição, Estado e Cidade andam juntos (Thiago, 08/08): não existe perfil
 * "Estado e Cidade sem Jurisdição". Por isso a exceção é binária — ou o tipo é
 * restringido pela regra do catálogo, ou não é restringido em nada.
 *
 * Esta lista é uma exceção CONSCIENTE porque o catálogo não tem hoje um sinal que
 * a expresse (`allow_cities` vem `all` até para tipos federais como a CNDT, então
 * não discrimina nada). Ela degrada sozinha: no dia em que um desses tipos ganhar
 * um filho de outra esfera no catálogo, ele vira multi-jurisdição e passa a exibir
 * a barra completa pela regra geral, sem depender desta lista.
 */
const TYPES_WITHOUT_FIELD_RESTRICTION = new Set(["outros", "divida-ativa", "trt", "trf"]);

/**
 * A jurisdição que governa QUAIS CAMPOS a aba exibe — a regra do catálogo
 * (`certificateSingleJurisdiction`) menos as exceções de produto acima.
 *
 * Quem consome isto é a barra de filtros (`buildCndFilterRows`) e as colunas
 * geográficas da tabela (`certificateGeoColumns`). A consulta ao backend e a chave
 * de layout da tabela continuam saindo de `props.jurisdiction`.
 *
 * ⚠️ A lista de exceção resolve o menu FLAT, onde a URL não informa esfera nenhuma.
 * No menu POR JURISDIÇÃO a URL vence (`props.jurisdiction ?? …` no painel) e a
 * exceção nem é consultada — DE PROPÓSITO. Ali o usuário escolheu a esfera
 * explicitamente, a consulta já recorta por ela, e oferecer um filtro de Jurisdição
 * dentro de "Estadual > Dívida Ativa" seria absurdo além de divergir de todos os
 * outros tipos do mesmo menu. É o contrato que existia antes da GEN-3157 e ele
 * segue valendo; há teste fixando isso para ninguém "corrigir" por engano.
 */
export const certificateFieldsJurisdiction = (certificate?: ICertificate): Jurisdiction | undefined => {
  if (certificate?.slug && TYPES_WITHOUT_FIELD_RESTRICTION.has(certificate.slug)) {
    return undefined;
  }
  return certificateSingleJurisdiction(certificate);
};

/**
 * Quais colunas geográficas a tabela deve exibir, dada a jurisdição que governa os
 * campos da aba.
 *
 * A regra é a MESMA da barra de filtros, de propósito: numa aba só federal não há
 * filtro de Estado/Cidade e a coluna correspondente vem sempre vazia — oferecer a
 * coluna sem o filtro é a incoerência que a GEN-3157 r2 fecha. Manter a decisão
 * aqui, num lugar só, é o que impede filtro e coluna de divergirem; o `switch`
 * espelha o de `buildCndFilterRows` e há teste amarrando os dois.
 *
 * A coluna Jurisdição NÃO entra: ela permanece em todas as abas (decisão de
 * produto — o dado da esfera é útil de ler mesmo onde não faz sentido filtrar).
 */
export interface CertificateGeoColumns {
  state: boolean;
  city: boolean;
}

export const certificateGeoColumns = (fieldsJurisdiction?: string | null): CertificateGeoColumns => {
  switch (fieldsJurisdiction) {
    case "federal":
      return { state: false, city: false };
    case "state":
      return { state: true, city: false };
    default:
      return { state: true, city: true };
  }
};
